import { apiSlice } from './apiSlice';
import { notificationListTag, notificationsApi } from './notificationsApi';
import type { TaskHistoryPage } from '../../types/taskHistory';

export interface CompletionProjection {
  physicalComplete: boolean;
  operationalComplete: boolean;
  financialComplete: boolean;
  workflowComplete: boolean;
  overallComplete: boolean;
  unresolved: string[];
}

export type WorkspaceTaskScope = 'ROUTE' | 'GROUP' | 'STOP';
export type WorkspaceTransportType = 'AUTO' | 'RAIL' | 'AIR';
export type WorkspaceStopType = 'loading' | 'unloading';

export interface WorkspaceTask {
  id: number;
  taskType: string;
  status: string;
  dueDate?: string | null;
  routeId?: number | null;
  applicationId?: number | null;
  entityType?: string | null;
  invoiceId?: number | null;
  obligationId?: number | null;
  obligationStage?: string | null;
  /** Phase 1D-A — task scope; legacy tasks: ROUTE with null scope ids. */
  taskScope?: WorkspaceTaskScope | null;
  groupId?: number | null;
  groupLabel?: string | null;
  routeStopId?: number | null;
  stopLabel?: string | null;
  stopType?: WorkspaceStopType | null;
  transportType?: WorkspaceTransportType | null;
  /** Статус связанного маршрута (если есть relation route) */
  routeStatus?: string | null;
  /** Краткий пункт погрузки (из адреса), если маршрут подгружен */
  loadingCity?: string | null;
  unloadingCity?: string | null;
  carrierName?: string | null;
  /** Договор-заявка перевозчика (CarrierAgreement), если маршрут подгружен к задаче. */
  carrierAgreement?: {
    number?: string | null;
    date?: string | null;
  } | null;
  applicationStatus?: string | null;
  applicationNumber?: string | null;
  /** Внешний номер (входящая заявка) */
  applicationExternalNumber?: string | null;
  applicationDate?: string | null;
  applicationCreatedAt?: string | null;
  /** Направление заявки: города из БД или временный fallback (без сырого полного адреса в UI) */
  applicationDirection?: string | null;
  applicationClientName?: string | null;
}

export interface WorkspaceRouteApplicationLink {
  applicationId: number;
  application?: {
    id: number;
    internalNumber?: string | null;
    client?: {
      id?: number | null;
      name?: string | null;
      shortName?: string | null;
    } | null;
  } | null;
}

export interface WorkspaceRouteStop {
  type?: string | null;
  position?: number | null;
  plannedAt?: string | null;
  actualAt?: string | null;
}

export interface WorkspaceRoute {
  id: number;
  number?: string | null;
  status: string;
  isCompanyRoute?: boolean;
  createdAt?: string | null;
  carrierContractRequestNumber?: string | null;
  carrierContractRequestDate?: string | null;
  plannedLoadingDate?: string | null;
  plannedUnloadingDate?: string | null;
  actualLoadingDate?: string | null;
  actualUnloadingDate?: string | null;
  client?: {
    id?: number | null;
    name?: string | null;
    shortName?: string | null;
  } | null;
  carrier?: {
    name?: string | null;
  } | null;
  carrierAgreement?: {
    number?: string | null;
    date?: string | null;
  } | null;
  applications?: WorkspaceRouteApplicationLink[] | null;
  stops?: WorkspaceRouteStop[] | null;
  controlTasks?: { id: number; status: string }[] | null;
  loadingCity?: string | null;
  unloadingCity?: string | null;
  direction?: string | null;
  completionProjection?: CompletionProjection | null;
}

export interface WorkspaceApplication {
  id: number;
  number: string;
  /** Внешний номер заявки (как в БД) */
  externalNumber?: string | null;
  /** Дата основания / входящей заявки клиента (в БД basis_date) */
  basisDate?: string | null;
  status: string;
  internalNumber?: string | null;
  client?: {
    name?: string | null;
    shortName?: string | null;
  } | null;
  /** Договор с клиентом (Contract), если заявка привязана */
  contract?: {
    number?: string | null;
    date?: string | null;
  } | null;
  loadingAddress?: string | null;
  unloadingAddress?: string | null;
  applicationDate?: string | null;
  createdAt?: string | null;
  direction?: string | null;
  completionProjection?: CompletionProjection | null;
  financialObligations?: Array<{
    id?: number;
    status?: string | null;
    stage?: string | null;
    direction?: string | null;
  }> | null;
}

export interface WorkspaceResponse {
  overdueCount?: number;
  taskCount?: number;
  routeCount?: number;
  applicationCount?: number;
  overdueTasks: WorkspaceTask[];
  myTasks: WorkspaceTask[];
  myRoutes: WorkspaceRoute[];
  myApplications: WorkspaceApplication[];
  cargoWithoutRoute?: unknown[];
  cargoInWarehouse?: unknown[];
}

/** Строка из GET /control/applications/:applicationId/tasks (как у маршрута). */
export interface ApplicationControlTaskRow {
  id: number;
  taskType: string;
  status: string;
  dueDate?: string | null;
  completedAt?: string | null;
  createdAt: string;
  entityType?: string | null;
  applicationId?: number | null;
  invoiceId?: number | null;
  obligationId?: number | null;
  obligationStage?: string | null;
}

export const workspaceApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getWorkspace: builder.query<WorkspaceResponse, void>({
      query: () => '/workspace',
      providesTags: ['Workspace'],
    }),
    getApplicationControlTasks: builder.query<ApplicationControlTaskRow[], number>({
      query: (applicationId) => `/control/applications/${applicationId}/tasks`,
      transformResponse: (response: { data?: ApplicationControlTaskRow[] } | ApplicationControlTaskRow[]) => {
        const raw = response && typeof response === 'object' && 'data' in response ? response.data : response;
        return Array.isArray(raw) ? raw : [];
      },
      providesTags: (result, error, applicationId) => [{ type: 'ApplicationControlTasks', id: applicationId }],
    }),
    getRouteTaskHistory: builder.query<TaskHistoryPage, number>({
      query: (routeId) => `/control/routes/${routeId}/task-history`,
      transformResponse: (response: { data?: TaskHistoryPage } | TaskHistoryPage) => {
        const raw = response && typeof response === 'object' && 'data' in response ? response.data : response;
        if (raw && typeof raw === 'object' && 'items' in raw && Array.isArray((raw as TaskHistoryPage).items)) {
          return raw as TaskHistoryPage;
        }
        return { items: [], total: 0, limit: 200, offset: 0, hasMore: false };
      },
      providesTags: (result, error, routeId) => [{ type: 'RouteTaskHistory', id: routeId }],
    }),
    getApplicationTaskHistory: builder.query<TaskHistoryPage, number>({
      query: (applicationId) => `/control/applications/${applicationId}/task-history`,
      transformResponse: (response: { data?: TaskHistoryPage } | TaskHistoryPage) => {
        const raw = response && typeof response === 'object' && 'data' in response ? response.data : response;
        if (raw && typeof raw === 'object' && 'items' in raw && Array.isArray((raw as TaskHistoryPage).items)) {
          return raw as TaskHistoryPage;
        }
        return { items: [], total: 0, limit: 200, offset: 0, hasMore: false };
      },
      providesTags: (result, error, applicationId) => [
        { type: 'ApplicationTaskHistory', id: applicationId },
      ],
    }),
    completeControlTask: builder.mutation<void, number>({
      query: (id: number) => ({
        url: `/control/tasks/${id}/done`,
        method: 'PATCH',
      }),
      invalidatesTags: ['Workspace', 'ApplicationControlTasks', 'RouteTaskHistory', 'ApplicationTaskHistory', notificationListTag],
      async onQueryStarted(_id, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(notificationsApi.util.invalidateTags([notificationListTag]));
        } catch {
          /* mutation failed */
        }
      },
    }),
    cancelControlTask: builder.mutation<void, number>({
      query: (id: number) => ({
        url: `/control/tasks/${id}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: ['Workspace', 'ApplicationControlTasks', 'RouteTaskHistory', 'ApplicationTaskHistory', notificationListTag],
      async onQueryStarted(_id, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(notificationsApi.util.invalidateTags([notificationListTag]));
        } catch {
          /* mutation failed */
        }
      },
    }),
  }),
});

export const {
  useGetWorkspaceQuery,
  useGetApplicationControlTasksQuery,
  useGetRouteTaskHistoryQuery,
  useGetApplicationTaskHistoryQuery,
  useCompleteControlTaskMutation,
  useCancelControlTaskMutation,
} = workspaceApi;

